<template>
  <div class="file-uploader" @click.stop="openPicker">
    <div
      class="upload-container"
      :class="{ dragover: isOver }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      role="button"
      tabindex="0"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
    >
      <div>
        <input
          ref="input"
          type="file"
          name="file"
          style="display: none;"
          :accept="accept"
          :multiple="multiple"
          @change="onChange"
        >
        <label>{{ fileName || placeholder }}</label>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'ImportDropzone',
  props: {
    accept: {
      type: String,
      default: '.csv,.xlsx,.txt,text/csv,text/plain'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Выберите файл или перетащите его сюда'
    }
  },
  data () {
    return {
      isOver: false,
      fileName: ''
    }
  },
  methods: {
    readAsText(file){
  const r = new FileReader()
  r.onload  = e => this.$emit('uploaded', String(e.target.result || ''))
  r.onerror = () => this.$emit('error', 'Не удалось прочитать файл')
  r.readAsText(file, 'utf-8')
},
    openPicker () {
      this.$refs.input && this.$refs.input.click()
    },
    onChange (e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if(!files.length) return

      const selected = this.multiple ? files : files.slice(0 , 1)
      const allowed = selected.filter(f => this.isTypeAllowed(f))
      if(!allowed.length){
        this.$emit('error', 'Непотдежривается')
        return
      }
      this.fileName = allowed.length > 1 ? `${allowed.length} файла` : allowed[0].name
      allowed.forEach(f => this.readFile(f))
    },

    onDragOver () { this.isOver = true },
    onDragLeave () { this.isOver = false },

    onDrop (e) {
      this.isOver = false;
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;

      const selected = this.multiple ? files : files.slice(0, 1);
      const allowed = selected.filter(f => this.isTypeAllowed(f));
      if (!allowed.length) { 
        this.$emit('error', 'Непотдежривается') 
        return
      }

      this.fileName = allowed.length > 1 ? `${allowed.length} файла` : allowed[0].name;
      allowed.forEach(f => this.readFile(f));
    },

    isTypeAllowed (file) {
      const accept = this.accept.split(',').map(s => s.trim().toLowerCase())
      const mime = (file.type || '').toLowerCase()
      const name = (file.name || '').toLowerCase()
      return accept.some(rule => {
        if (!rule) return true
        if (rule.startsWith('.')) return name.endsWith(rule)
        return mime === rule
      })
    },
    async readFile (file) {
      const ext = (file.name || '').split('.').pop().toLowerCase()

      if (ext === 'xlsx') {
        const ab = await file.arrayBuffer()
        const mod = await import('exceljs/dist/exceljs.min.js')
        const ExcelJS = mod.default || mod
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(ab)
        const ws = wb.worksheets[0]
        if (!ws) {
          this.$emit('error', 'Лист Excel не найден')
          return
        }

        const rows = [] 
        const lines = []
        lines.push(['Группа', 'Ключевое слово'].join('\t'))

        ws.eachRow((row, idx) => {
          const A = this._cellToString(row.getCell(1))
          const B = this._cellToString(row.getCell(2))
          if (!A && !B) return
          if (idx === 1 && /группа/i.test(A) && /ключ/i.test(B)) return
          rows.push([A,B])
          lines.push([A, B].join('\t'))
        })

        this.$emit('parsed', rows)
        this.$emit('uploaded', lines.join('\n'))
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const buf = e.target.result
          let text = ''
          try { text = new TextDecoder('utf-8').decode(buf) } catch (e) {/* ignore: fallback to cp1251 below */}
          if (!text || text.includes('�') || !/[А-Яа-яЁё]/.test(text)) {
            try { text = new TextDecoder('windows-1251').decode(buf) } catch (e) {/* ignore: fallback to cp1251 below */}
          }
          this.$emit('uploaded', String(text || ''))
        } catch (err) {
          this.$emit('error', 'Ошибка декодирования файла')
        }
      }
      reader.onerror = () => this.$emit('error', 'Не удалось прочитать файл')
      reader.readAsArrayBuffer(file)
    },

    _cellToString (cell) {
      const v = cell && cell.value
      if (v == null) return ''
      if (typeof v === 'object' && v.text) return String(v.text).trim()
      return String(v).trim()
    }
  }
}
</script>

<style scoped>

.p-2 { padding: 2%; }
.w-100 { width: 100% }

.file-uploader {
  display: flex;
  padding: 1em;
  width: 100%;
  justify-content: center;
}

.upload-container:hover { background: #3745be2b; }

.upload-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 200px;
  height: 200px;
  outline: 2px dashed #5d5d5d;
  outline-offset: -12px;
  background: #4B5EFF2B;
  font-family: "Segoe UI";
  color: #1f3c44;
  cursor: pointer;
}

.upload-container img {
  width: 40%;
  margin-bottom: 20px;
  user-select: none;
}

.upload-container label:hover {
  cursor: pointer;
  text-decoration: underline;
}

.upload-container div {
  position: relative;
  z-index: 10;
}

.upload-container input[type="file"] {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  position: absolute;
  z-index: -10;
}

.upload-container label.focus {
  outline: 1px solid #0078d7;
  outline: -webkit-focus-ring-color auto 5px;
}

.upload-container.dragover {
  background-color: #fafafa;
  outline-offset: -17px;
}

</style>
